import React, { useEffect, useState, useCallback } from "react";
import { Redirect } from "react-router-dom";
import { Field, Form } from "react-final-form";
import { InputText } from "primereact/inputtext";
import { classNames } from "primereact/utils";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import "./FullClaimInfoForm.css";
import { Checkbox } from "primereact/checkbox";
import { MultiSelect } from "primereact/multiselect";
import { fetchCarrierItemFromLS } from "../../services/local-storage";
import NewAddressForm from "../NewAddressForm";
import { camelToTitle } from "../../utils";
import FormItem from "./FormItem";
import {
  damageTypes,
  titleBrands,
  cosmeticEligibleDamages,
  titleLienTypes,
  causeOfLossOptions,
  regexPatterns
} from "../../services/constants";
import { buildAddressStringFromObject } from "../../services/formatting";

const getAddressFields = (xTitleSelected, mode) => {
  if (xTitleSelected) {
    return [
      "ownersAddress",
      "acquiringPartyMailingAddress",
      "acquiringPartyPhysicalAddress",
      "disposingPartyAddress"
    ];
  }
  if (mode === "create") {
    return ["ownersAddress", "titleRemittanceAddress"];
  }
  return ["ownersAddress", "titleRemittanceAddress"];
};

const isFormFieldValid = meta => !!(meta.touched && meta.error);

const FullClaimInfoForm = ({
  stepperRef,
  packet,
  xTitleSelected,
  existingPacket,
  selectedOrders,
  fullClaimInfo,
  setFullClaimInfo,
  submitClaim,
  requiredFields,
  optionalFields,
  carrier,
  submitting,
  states,
  mode,
  testing
}) => {
  const [initialValues, setInitialValues] = useState({});
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [focusedGroup, setFocusedGroup] = useState();
  const [touchedGroups, setTouchedGroups] = useState([]);
  const [selectedDamages, setSelectedDamages] = useState([]);
  const [titleBrand, setTitleBrand] = useState();
  const [titleType, setTitleType] = useState();
  const [availableTitleBrands, setAvailableTitleBrands] = useState(null);
  const [availableTitleTypes, setAvailableTitleTypes] = useState(
    titleLienTypes
  );
  const [titleTransferDate, setTitleTransferDate] = useState(new Date());
  const [physicalSameAsMailing, setPhysicalSameAsMailing] = useState(false);
  const [redirect, setRedirect] = useState();
  const [traId, setTraId] = useState();
  const [formItemHelpers, setFormItemHelpers] = useState({
    requiredFields,
    optionalFields,
    handleFocus: () => {},
    isFormFieldValid,
    classNames,
    getFormErrorMessage: () => {},
    getAddressErrorMessage: () => {}
  });

  const user = JSON.parse(fetchCarrierItemFromLS("user"));
  const odometerReadingMaxDate = new Date();

  useEffect(() => {
    setInitialValues(fullClaimInfo);
  }, [fullClaimInfo]);

  // set odometer requirements
  const handleOdometerExempt = event => {
    const odometerExempt = event.target.checked;
    if (odometerExempt) {
      // set odometerExemptReason to required
      if (!requiredFields.includes("odometerExemptReason")) {
        requiredFields.push("odometerExemptReason");
      }
      // remove odometer and odometerReadingDate from required
      const odometerIndex = requiredFields.indexOf("odometer");
      if (odometerIndex > -1) {
        requiredFields.splice(odometerIndex, 1);
      }
      const odometerReadingDateIndex = requiredFields.indexOf(
        "odometerReadingDate"
      );
      if (odometerReadingDateIndex > -1) {
        requiredFields.splice(odometerReadingDateIndex, 1);
      }
    } else {
      // set odometer to required
      // set odometerReadingDate to required
      if (!requiredFields.includes("odometer")) {
        requiredFields.push("odometer");
      }
      if (!requiredFields.includes("odometerReadingDate")) {
        requiredFields.push("odometerReadingDate");
      }
      // remove odometerExemptReason from required
      const odometerExemptReasonIndex = requiredFields.indexOf(
        "odometerExemptReason"
      );
      if (odometerExemptReasonIndex > -1) {
        requiredFields.splice(odometerExemptReasonIndex, 1);
      }
    }
  };

  // intercept enter so it doesn't submit the form or go back
  // there's probably a better way to handle this
  useEffect(() => {
    const handleKeyDown = event => {
      if (event.key === "Enter") {
        event.preventDefault(); // Optional: prevent default Enter key behavior
        return;
      }
    };
    // Add event listener
    window.addEventListener("keydown", handleKeyDown);
    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // restructure values from either packet or existing form data to prepopulate form
  useEffect(() => {
    if (existingPacket) {
      const transformedValues = JSON.parse(JSON.stringify(existingPacket));
      transformedValues.dateOfLoss = new Date(transformedValues.dateOfLoss);
      transformedValues.ownersAddress = {
        streetAddress: transformedValues.ownersStreetAddress,
        streetAddress2: transformedValues.ownersStreetAddress2,
        zipCode: transformedValues.ownersZipCode,
        city: transformedValues.ownersCity,
        state: states?.find(
          state => state.stateId === transformedValues.ownersStateId
        )
      };
      transformedValues.vehicle = {
        year: transformedValues.year
      };
      transformedValues.adjusterInfo = {
        adjusterName: transformedValues?.adjusterName || user?.name,
        adjusterEmailAddress:
          transformedValues?.adjusterEmailAddress || user?.emailAddress,
        adjusterPhoneNumber:
          transformedValues?.adjusterPhoneNumber || user?.phoneNumber
      };
      if (Object.keys(transformedValues.titleTransfer).length > 0) {
        transformedValues.odometerReadingDate = new Date(
          transformedValues.titleTransfer.odometerReadDate
        );

        if (
          selectedDamages?.length !==
            transformedValues.titleTransfer.damages?.length &&
          !initialLoaded
        ) {
          if (
            transformedValues.titleTransfer?.damages &&
            transformedValues.titleTransfer.damages.length !== 0
          ) {
            const damages = transformedValues?.titleTransfer?.damages.map(d => {
              return damageTypes.find(e => e.value === d);
            });
            setSelectedDamages(damages);
          } else {
            setSelectedDamages([]);
            transformedValues.noDamages = true;
          }
        }
        if (
          titleBrand?.label !==
            transformedValues?.titleTransfer?.requestedBrand &&
          !initialLoaded
        ) {
          setAvailableTitleBrands([
            {
              label: transformedValues?.titleTransfer?.requestedBrand,
              value: transformedValues?.titleTransfer?.requestedBrand
            }
          ]);
          setTitleBrand({
            label: transformedValues.titleTransfer.requestedBrand,
            value: transformedValues.titleTransfer.requestedBrand
          });
        }
        if (
          titleType?.label !==
            transformedValues.titleTransfer.requestedFormat &&
          !initialLoaded
        ) {
          setAvailableTitleTypes([
            {
              label: transformedValues.titleTransfer.requestedFormat,
              value: transformedValues.titleTransfer.requestedFormat
            }
          ]);
          setTitleType({
            label: transformedValues.titleTransfer.requestedFormat,
            value: transformedValues.titleTransfer.requestedFormat
          });
        }

        // default titleRemittanceAddress to acquiringPartyPhysicalAddress
        // we store regular titleRemittanceAddress in titleTransfer even if there's
        // no title transfer order
        if (transformedValues?.titleTransfer?.acquiringPartyPhysicalAddress) {
          transformedValues.titleRemittanceAddress = {
            streetAddress:
              transformedValues.titleTransfer?.acquiringPartyPhysicalAddress
                ?.streetAddress,
            streetAddress2:
              transformedValues.titleTransfer?.acquiringPartyPhysicalAddress
                ?.streetAddress2,
            city:
              transformedValues.titleTransfer?.acquiringPartyPhysicalAddress
                ?.city,
            state: states?.find(
              state =>
                state?.stateName ===
                transformedValues.titleTransfer?.acquiringPartyPhysicalAddress
                  ?.state
            ),
            zipCode:
              transformedValues.titleTransfer?.acquiringPartyPhysicalAddress
                ?.zipCode
          };
        }

        transformedValues.titleNumber =
          transformedValues.titleTransfer?.titleNumber;
        transformedValues.acquiringPartyName =
          transformedValues.titleTransfer?.acquiringPartyName;
        transformedValues.acquiringPartyEmail =
          transformedValues.titleTransfer?.acquiringPartyEmail;
        transformedValues.disposingPartyName =
          transformedValues.titleTransfer?.disposingPartyName;
        transformedValues.acquiringPartyMailingAddress = {
          streetAddress:
            transformedValues.titleTransfer?.acquiringPartyMailingAddress
              ?.streetAddress,
          streetAddress2:
            transformedValues.titleTransfer?.acquiringPartyMailingAddress
              ?.streetAddress2,
          city:
            transformedValues.titleTransfer?.acquiringPartyMailingAddress?.city,
          state: states?.find(
            state =>
              state?.stateName ===
                transformedValues.titleTransfer?.acquiringPartyMailingAddress
                  ?.state ||
              state?.stateInitials ===
                transformedValues.titleTransfer?.acquiringPartyMailingAddress
                  ?.state
          ),
          zipCode:
            transformedValues.titleTransfer?.acquiringPartyMailingAddress
              ?.zipCode
        };
        transformedValues.acquiringPartyPhysicalAddress = {
          streetAddress:
            transformedValues.titleTransfer?.acquiringPartyPhysicalAddress
              ?.streetAddress,
          streetAddress2:
            transformedValues.titleTransfer?.acquiringPartyPhysicalAddress
              ?.streetAddress2,
          city:
            transformedValues.titleTransfer?.acquiringPartyPhysicalAddress
              ?.city,
          state: states?.find(
            state =>
              state?.stateName ===
                transformedValues.titleTransfer?.acquiringPartyPhysicalAddress
                  ?.state ||
              state?.stateInitials ===
                transformedValues.titleTransfer?.acquiringPartyPhysicalAddress
                  ?.state
          ),
          zipCode:
            transformedValues.titleTransfer?.acquiringPartyPhysicalAddress
              ?.zipCode
        };
        transformedValues.disposingPartyName =
          transformedValues.titleTransfer.disposingPartyName;
        transformedValues.disposingPartyAddress = {
          streetAddress:
            transformedValues.titleTransfer?.disposingPartyAddress
              ?.streetAddress,
          streetAddress2:
            transformedValues.titleTransfer?.disposingPartyAddress
              ?.streetAddress2,
          city: transformedValues.titleTransfer?.disposingPartyAddress?.city,
          state: states?.find(
            state =>
              state?.stateName ===
                transformedValues.titleTransfer?.disposingPartyAddress?.state ||
              state?.stateInitials ===
                transformedValues.titleTransfer?.disposingPartyAddress?.state
          ),
          zipCode:
            transformedValues.titleTransfer?.disposingPartyAddress?.zipCode
        };
        transformedValues.odometerExempt =
          transformedValues.titleTransfer?.odometerExempt;
        transformedValues.odometerExemptReason =
          transformedValues.titleTransfer?.odometerExemptReason;
        const transferDate = new Date(
          transformedValues.titleTransfer?.titleTransferDate
        );
        transformedValues.titleTransferDate = transferDate.toString();
        if (
          titleTransferDate.toString() !== transferDate.toString() &&
          !initialLoaded
        ) {
          setTitleTransferDate(transferDate);
        }
      }

      if (JSON.stringify(transformedValues) !== JSON.stringify(initialValues)) {
        setInitialValues(transformedValues);
      }
    } else {
      const initValues = initialValues;
      initValues.adjusterInfo = {
        adjusterName: initValues?.adjusterInfo?.adjusterName || user?.name,
        adjusterEmailAddress:
          initValues?.adjusterInfo?.adjusterEmailAddress || user?.emailAddress,
        adjusterPhoneNumber:
          initValues?.adjusterInfo?.adjusterPhoneNumber || user?.phoneNumber
      };
      // if there's not already an initial title remittance address
      // and userRemittanceAddresses is true, set the user address
      if (
        !initValues.titleRemittanceAddress &&
        carrier?.userRemittanceAddresses
      ) {
        initValues.titleRemittanceAddress = {
          streetAddress: user?.mailingStreetAddress,
          streetAddress2: user?.mailingStreetAddress2,
          city: user?.mailingCity,
          state: states?.find(
            state => state.stateInitials === user?.mailingState
          ),
          zipCode: user?.mailingZipCode
        };
      }
      if (JSON.stringify(initValues) !== JSON.stringify(initialValues)) {
        setInitialValues(initValues);
      }
    }
    if (!initialLoaded) {
      setInitialLoaded(true);
    }
  }, [
    existingPacket,
    user,
    states,
    initialValues,
    selectedDamages,
    titleBrand,
    titleType,
    initialLoaded,
    carrier,
    selectedOrders,
    titleTransferDate
  ]);

  // handles when an address group has been focused
  // so we don't collapse it before the user has a chance to fill it out
  const handleFocus = useCallback(
    name => {
      let focused;
      let touched = [...touchedGroups];
      if (getAddressFields(xTitleSelected).includes(name)) {
        focused = name;
      }

      if (
        name !== focusedGroup &&
        getAddressFields(xTitleSelected).includes(focusedGroup) &&
        !touchedGroups.includes(focusedGroup)
      ) {
        touched.push(focusedGroup);
      }

      setFocusedGroup(focused);
      if (touchedGroups?.length !== touched?.length) {
        setTouchedGroups(touched);
      }
    },
    [focusedGroup, touchedGroups, xTitleSelected]
  );

  const validate = (data, touched = touchedGroups) => {
    let errors = {};
    const specialMessages = {
      damages: "At least one damage type is required."
    };
    const addressLines = ["streetAddress", "city", "state", "zipCode"];
    // loop over address fields and check if they are present
    // remove touched status if no errors
    for (const field of getAddressFields(xTitleSelected, mode)) {
      if (!requiredFields.includes(field)) {
        continue;
      }
      if (touched.includes(field)) {
        let showError = false;
        // don't validate physical address if same as mailing
        if (
          field === "acquiringPartyPhysicalAddress" &&
          physicalSameAsMailing
        ) {
          continue;
        }
        for (const line of addressLines) {
          let l = data?.[field]?.[line];
          if (line === "state" && typeof l === "object") {
            l = l?.stateName;
          }
          if (!l || (!data?.[field]?.["prefilledAddress"] && l.length === 0)) {
            showError = true;
            break;
          }
        }

        if (showError) {
          errors[field] = `${camelToTitle(field)} is required.`;
        } else {
          errors[field] = null;
        }
      }
    }

    // pull in the values we're storing in state so they can be treated normally
    data["damages"] = data["noDamages"] ? ["No Damages"] : selectedDamages;
    data["titleBrand"] =
      typeof titleBrand === "string" ? titleBrand : titleBrand?.label;
    data["titleType"] =
      typeof titleType === "string" ? titleType : titleType?.label;
    data["titleTransferDate"] = titleTransferDate;

    // handle normal validations
    requiredFields.forEach(field => {
      if (
        (!data[field] || data[field].length === 0) &&
        !getAddressFields(xTitleSelected).includes(field)
      ) {
        errors[field] =
          specialMessages[field] || `${camelToTitle(field)} is required.`;
      }
    });

    // handle special validations
    if (
      !regexPatterns.email.test(data?.ownersEmailAddress) &&
      data.ownersEmailAddress
    ) {
      errors.ownersEmailAddress = "Invalid email address.";
    }
    if (
      !regexPatterns.email.test(data?.adjusterInfo?.adjusterEmailAddress) &&
      data?.adjusterInfo?.adjusterEmailAddress
    ) {
      errors.adjusterInfo = {
        ...errors.adjusterInfo,
        adjusterEmailAddress: "Invalid email address."
      };
    }
    if (
      !regexPatterns.email.test(data?.acquiringPartyEmail) &&
      data?.acquiringPartyEmail
    ) {
      errors.acquiringPartyEmail = "Invalid email address.";
    }
    if (
      !regexPatterns.phone.test(data?.adjusterInfo?.adjusterPhoneNumber) &&
      data?.adjusterInfo?.adjusterPhoneNumber != null &&
      data?.adjusterInfo?.adjusterPhoneNumber?.length > 0
    ) {
      errors.adjusterInfo = {
        ...errors.adjusterInfo,
        adjusterPhoneNumber: "Invalid phone number."
      };
    }
    if (
      !regexPatterns.phone.test(data?.ownersPhoneNumber) &&
      data?.ownersPhoneNumber != null &&
      data?.ownersPhoneNumber?.length > 0
    ) {
      errors.ownersPhoneNumber = "Invalid phone number.";
    }
    const nextYear = new Date().getFullYear() + 1;
    if (
      parseInt(data?.vehicle?.year) > nextYear ||
      parseInt(data?.vehicle?.year) < 1900
    ) {
      errors.vehicle = {
        ...errors?.vehicle,
        year: "Invalid Vehicle Year"
      };
    }
    if (
      requiredFields.includes("vehicle") &&
      !regexPatterns.vinYear.test(String(data?.vehicle?.year))
    ) {
      errors.vehicle = {
        ...errors?.vehicle,
        year: "Invalid year."
      };
    }
    if (parseFloat(data?.settlementAmount) > 999999.99) {
      errors.settlementAmount =
        "Settlement amount must be less than $1,000,000.";
    }
    if (
      data?.settlementAmount &&
      !regexPatterns.money.test(data?.settlementAmount)
    ) {
      errors.settlementAmount = "Settlement Amount must be a dollar value";
    }

    if (parseFloat(data?.deductible) > 999999.99) {
      errors.deductible = "Deductible must be less than $1,000,000.";
    }
    if (data?.deductible && !regexPatterns.money.test(data?.deductible)) {
      errors.deductible = "Deductible must be a dollar value";
    }
    return errors;
  };

  const devPopulate = data => {
    data.change("causeOfLoss", "Fire");
    data.change("ownersName", "John Doe");
    data.change("dateOfLoss", new Date());
    data.change("deductible", "500");
    data.change("ownersEmailAddress", "test@test.com");
    data.change("ownersPhoneNumber", "1234567890");
    data.change("ownersAddress.streetAddress", "123 Main St");
    data.change("ownersAddress.city", "Anytown");
    data.change("ownersAddress.state", states[0]);
    data.change("ownersAddress.zipCode", "12345");
    data.change("vehicle.year", "2000");
    data.change("settlementAmount", "1000");
    data.change("odometerExempt", false);
    data.change("odometer", "100000");
    data.change("odometerReadingDate", new Date());
    setSelectedDamages([{ label: "Fire", value: "FIRE" }]);
    setAvailableTitleBrands([{ label: "Fire", value: "FIRE" }]);
    setTitleBrand({ label: "Fire", value: "FIRE" });
    // setAvailableTitleTypes([{ label: "Paper", value: "Paper" }]);
    setTitleType({ label: "Paper", value: "Paper" });
    data.change("titleTransferDate", new Date());
    data.change("titleNumber", "1234567890");
    data.change("acquiringPartyName", "Jane Doe");
    data.change("acquiringPartyEmail", "jane.doe@test.com");
    data.change("acquiringPartyMailingAddress.streetAddress", "123 Main St");
    data.change("acquiringPartyMailingAddress.city", "Anytown");
    data.change("acquiringPartyMailingAddress.state", states[0]);
    data.change("acquiringPartyMailingAddress.zipCode", "12345");
    data.change("acquiringPartyPhysicalAddress.streetAddress", "321321 street");
    data.change("acquiringPartyPhysicalAddress.streetAddress2", "Suite 100");
    data.change("acquiringPartyPhysicalAddress.city", "Townsville");
    data.change("acquiringPartyPhysicalAddress.state", states[0]);
    data.change("acquiringPartyPhysicalAddress.zipCode", "66666");
    data.change("disposingPartyName", "Jane Doe");
    data.change("disposingPartyAddress.streetAddress", "123 Main St");
    data.change("disposingPartyAddress.streetAddress2", "Suite 100");
    data.change("disposingPartyAddress.city", "Anytown");
    data.change("disposingPartyAddress.state", states[0]);
    data.change("disposingPartyAddress.zipCode", "12345");
    data.change("ownerRetained", true);
    data.change("vehicleLocation", "123 Main St, Anytown, ST 12345");
  };

  const isFormFieldShown = (form, name) => {
    return form.getFieldState(name)?.value;
  };

  const validateYear = (event, validatePattern) => {
    const target = event.target;
    if (validatePattern) {
    } else {
      if (target.value.length > 0) {
        target.value = target.value.slice(0, target.value.length - 1);
      }
    }
  };

  const goBack = form => {
    form.reset();
    stepperRef.current.prevCallback();
  };

  const getFormErrorMessage = useCallback(meta => {
    return (
      isFormFieldValid(meta) && <small className="p-error">{meta.error}</small>
    );
  }, []);

  const getAddressErrorMessage = useCallback(
    (address, form) => {
      const addressError = form.getState().errors?.[address];
      if (addressError && touchedGroups.includes(address)) {
        return (
          <small className="p-error">
            {Object.values(addressError)?.join("")}
          </small> // TODO: why does this turn into an object instead of a string
        );
      }
    },
    [touchedGroups]
  );

  // handles setting available titleBrands and titleTypes based on selected damages
  const selectDamage = (damages, noDamages) => {
    const currentDamages = selectedDamages;
    const newDamages = damages?.selectedOption;
    const damageExists =
      currentDamages?.length > 0 &&
      currentDamages.map(d => d?.value).includes(newDamages?.value);
    let allDamages;
    if (currentDamages?.length === 0) {
      allDamages = [newDamages];
    }
    if (currentDamages?.length > 0) {
      allDamages =
        damageExists && currentDamages?.length > 0
          ? currentDamages.filter(d => d?.value !== newDamages?.value)
          : [...currentDamages, newDamages];
    }
    if (noDamages) {
      setSelectedDamages([]);
      setTitleBrand(null);
      setAvailableTitleBrands(
        titleBrands.paper.concat(titleBrands.digital).concat(titleBrands.mixed)
      );
      setAvailableTitleTypes(titleLienTypes);
      return;
    } else if (allDamages.length === 1 && allDamages[0]?.value === "FIRE") {
      setTitleBrand({ label: "Fire", value: "FIRE" });
      setAvailableTitleBrands([{ label: "Fire", value: "FIRE" }]);
      setAvailableTitleTypes([{ label: "Paper", value: "Paper" }]);
      setTitleType({ label: "Paper", value: "Paper" });
    } else if (allDamages.length === 1 && allDamages[0]?.value === "FLOOD") {
      setTitleBrand({ label: "Flood", value: "FLOOD" });
      setAvailableTitleBrands([{ label: "Flood", value: "FLOOD" }]);
      setAvailableTitleTypes([{ label: "Paper", value: "Paper" }]);
      setTitleType({ label: "Paper", value: "Paper" });
    } else if (
      allDamages.length === 2 &&
      allDamages.map(d => d.value).includes("FIRE") &&
      allDamages.map(d => d.value).includes("FLOOD")
    ) {
      setTitleBrand(null);
      setAvailableTitleBrands([
        { label: "Fire", value: "FIRE" },
        { label: "Flood", value: "FLOOD" }
      ]);
    } else {
      let availableBrands = titleBrands.paper
        .concat(titleBrands.digital)
        .concat(titleBrands.mixed);
      allDamages.forEach(d => {
        if (!cosmeticEligibleDamages.includes(d?.value)) {
          availableBrands = availableBrands.filter(
            e => e.value !== "COSMETIC_TOTAL_LOSS"
          );
        }
      });
      setTitleBrand(null);
      setAvailableTitleBrands(availableBrands);
      setAvailableTitleTypes(titleLienTypes);
    }
    if (allDamages[0] != null) {
      setSelectedDamages(allDamages);
    } else {
      setSelectedDamages([]);
    }
  };

  // handles setting available titleTypes based on selected titleBrand
  const selectTitleBrand = brand => {
    const brandObject = titleBrands.paper
      .concat(titleBrands.digital)
      .concat(titleBrands.mixed)
      .find(e => e.value === brand);
    setTitleBrand(brandObject);
    if (titleBrands.digital.some(e => e.label === brandObject.label)) {
      setAvailableTitleTypes([{ label: "Digital", value: "Digital" }]);
      setTitleType({ label: "Digital", value: "Digital" });
    } else if (titleBrands.paper.some(e => e.label === brandObject.label)) {
      setAvailableTitleTypes([{ label: "Paper", value: "Digital" }]);
      setTitleType({ label: "Paper", value: "Digital" });
    } else {
      setAvailableTitleTypes(titleLienTypes);
      setTitleType(null);
    }
  };

  const selectTitleType = type => {
    const typeObject = { label: type, value: type };
    setTitleType(typeObject);
  };

  const submitFullClaimInfo = async form => {
    // set all fields as touched
    const allAddressFields = getAddressFields(xTitleSelected, mode);
    setTouchedGroups(allAddressFields);
    const values = form.getState().values;
    const errors = validate(values, allAddressFields);
    if (Object.values(errors).some(e => e)) {
      return;
    }
    const extra = {
      titleTransferDate,
      damages: selectedDamages.map(d => d?.value),
      requestedTitleBrand: titleBrand?.value,
      requestedTitleType: titleType?.label,
      titleRemittanceAddressId: traId
    };
    await submitClaim(values, extra, mode === "edit");
    // redirect to claim
    const url = `/packets/${packet.packetId}`;
    setRedirect(url);
  };

  // set formItemHelpers to minimize the number of props we pass
  useEffect(() => {
    setFormItemHelpers({
      requiredFields,
      optionalFields,
      handleFocus,
      isFormFieldValid,
      classNames,
      getFormErrorMessage,
      getAddressErrorMessage
    });
  }, [
    requiredFields,
    optionalFields,
    handleFocus,
    getFormErrorMessage,
    getAddressErrorMessage
  ]);

  return (
    <div className={"FullClaimInfoFormWrapper"}>
      {redirect && <Redirect to={redirect} />}
      <Form
        onSubmit={() => {}}
        initialValues={initialValues}
        validate={validate}
        render={({ handleSubmit, form }) => (
          <form onSubmit={handleSubmit} className="p-fluid">
            <h2 className={"SubsectionHeader"}>Required Data</h2>
            {requiredFields.includes("causeOfLoss") && (
              <FormItem
                name={"causeOfLoss"}
                label={"Cause of Loss"}
                type={"dropdown"}
                h={formItemHelpers}
                extra={{ options: causeOfLossOptions }}
              />
            )}
            {requiredFields.includes("dateOfLoss") && (
              <FormItem
                name={"dateOfLoss"}
                label={"Date of Loss"}
                type={"date"}
                h={formItemHelpers}
                extra={{ maxDate: odometerReadingMaxDate }}
              />
            )}
            {requiredFields.includes("settlementAmount") && (
              <FormItem
                name={"settlementAmount"}
                label={"Settlement Amount (Max Payoff)"}
                h={formItemHelpers}
                extra={{
                  keyfilter: "money",
                  addon: "$"
                }}
              />
            )}
            {requiredFields.includes("deductible") && (
              <FormItem
                name={"deductible"}
                label={"Deductible"}
                h={formItemHelpers}
                extra={{
                  keyfilter: "money",
                  addon: "$"
                }}
              />
            )}
            <h2 className={"SubsectionHeader"}>Lien Information</h2>
            {carrier?.showAdjusterInfoOnForm && (
              <>
                <FormItem
                  name={"adjusterInfo.adjusterName"}
                  label={"Adjusters Name"}
                  h={formItemHelpers}
                />
                <FormItem
                  name={"adjusterInfo.adjusterPhoneNumber"}
                  label={"Adjusters Phone Number"}
                  h={formItemHelpers}
                  type={"phoneNumber"}
                  extra={{
                    onInput: validate
                  }}
                />
                <FormItem
                  name={"adjusterInfo.adjusterEmailAddress"}
                  label={"Adjusters Email Address"}
                  h={formItemHelpers}
                />
              </>
            )}
            {requiredFields.includes("ownersName") && (
              <FormItem
                name={"ownersName"}
                label={"Owners Name"}
                h={formItemHelpers}
              />
            )}
            {(requiredFields.includes("ownersEmailAddress") ||
              optionalFields.includes("ownersEmailAddress")) && (
              <FormItem
                type={"text"}
                name={"ownersEmailAddress"}
                label={"Owner Email Address"}
                h={formItemHelpers}
              />
            )}
            {(requiredFields.includes("ownersPhoneNumber") ||
              optionalFields.includes("ownersPhoneNumber")) && (
              <FormItem
                name={"ownersPhoneNumber"}
                label={"Owner Phone Number"}
                h={formItemHelpers}
                type={"phoneNumber"}
                extra={{
                  onInput: validate
                }}
              />
            )}
            {requiredFields.includes("ownersAddress") && (
              <div
                className={`AddressGrouper ${
                  getAddressErrorMessage("ownersAddress", form)
                    ? "AddressGroupError"
                    : ""
                }`}
                onFocus={() => handleFocus("ownersAddress")}
              >
                <h4 className={"GroupSectionHeader"}>Owners Address</h4>
                <NewAddressForm
                  formPrefix={"ownersAddress"}
                  initial={initialValues}
                  required={requiredFields.includes("ownersAddress")}
                  validate={validate}
                  buildAddressStringFromObject={buildAddressStringFromObject}
                  states={states}
                />
                <span>{getAddressErrorMessage("ownersAddress", form)}</span>
              </div>
            )}

            {/* only show odometer and tra if they're not going to be shown */}
            {/* in another field in the xTitle section */}
            {!xTitleSelected && (
              <>
                <FormItem
                  name={"odometer"}
                  label={"Odometer Reading (miles)"}
                  h={formItemHelpers}
                />
                <div
                  className={`AddressGrouper ${
                    getAddressErrorMessage("titleRemittanceAddress", form)
                      ? "AddressGroupError"
                      : ""
                  }`}
                  onFocus={() => handleFocus("titleRemittanceAddress")}
                >
                  <h4 className={"GroupSectionHeader"}>
                    Title Remittance Address
                  </h4>
                  <NewAddressForm
                    formPrefix={"titleRemittanceAddress"}
                    initial={initialValues}
                    addressChoices={
                      carrier?.userRemittanceAddresses
                        ? [
                            ...carrier.titleRemittanceAddresses,
                            {
                              name: "User Address",
                              streetAddress: user?.mailingStreetAddress,
                              streetAddress2: user?.mailingStreetAddress2,
                              city: user?.mailingCity,
                              state: states?.find(
                                state =>
                                  state.stateInitials === user?.mailingState
                              ),
                              zipCode: user?.mailingZipCode
                            }
                          ]
                        : carrier.titleRemittanceAddresses
                    }
                    creatable
                    required={requiredFields.includes("titleRemittanceAddress")}
                    validate={validate}
                    buildAddressStringFromObject={buildAddressStringFromObject}
                    states={states}
                    setTraId={setTraId}
                  />
                  <span>
                    {getAddressErrorMessage("titleRemittanceAddress", form)}
                  </span>
                </div>
              </>
            )}

            {xTitleSelected && (
              <>
                <h2 className={"SubsectionHeader"}>
                  Title Transfer Requirements
                </h2>
                <FormItem
                  name={"vehicle.year"}
                  label={"Vehicle Year"}
                  h={formItemHelpers}
                  extra={{
                    keyfilter: /^\d{1,4}$/,
                    validateOnly: true,
                    onInput: validateYear
                  }}
                />
                <FormItem
                  type={"checkbox"}
                  name={"odometerExempt"}
                  label={"Odometer Exempt"}
                  extra={{ onChange: e => handleOdometerExempt(e) }}
                  h={formItemHelpers}
                />

                {isFormFieldShown(form, "odometerExempt") && (
                  <>
                    <FormItem
                      name={"odometerExemptReason"}
                      label={"Odometer Exception Reason"}
                      h={formItemHelpers}
                    />
                  </>
                )}
                {!isFormFieldShown(form, "odometerExempt") && (
                  <>
                    <FormItem
                      name={"odometer"}
                      label={"Odometer Reading (miles)"}
                      h={formItemHelpers}
                      extra={{ keyfilter: /^\d{1,7}$/ }}
                    />
                    <FormItem
                      name={"odometerReadingDate"}
                      label={"Odometer Reading Date (Date Read)"}
                      type={"date"}
                      h={formItemHelpers}
                      extra={{ maxDate: odometerReadingMaxDate }}
                    />
                  </>
                )}

                {/* not breaking out into FormItem components since there's specific logic */}
                {requiredFields.includes("damages") && (
                  <Field
                    name="noDamages"
                    required={requiredFields.includes("damages")}
                    type={"checkbox"}
                    render={({ input, meta }) => (
                      <div className="field">
                        <span className="p-float-label">
                          <Checkbox
                            {...input}
                            onChange={e => {
                              input.onChange(e);
                              selectDamage([], e.checked);
                            }}
                          />
                          <label
                            htmlFor={input.name}
                            className={classNames({
                              "p-error": isFormFieldValid(meta, "noDamages"),
                              "checkbox-odometer": true
                            })}
                          >
                            No Damages*
                          </label>
                        </span>
                        {getFormErrorMessage(meta)}
                      </div>
                    )}
                  />
                )}
                {requiredFields.includes("damages") &&
                  !form.getState().values?.noDamages && (
                    <Field
                      name="damages"
                      required={requiredFields.includes("damages")}
                      render={({ input, meta }) => (
                        <div className="field">
                          <span className="p-float-label">
                            <MultiSelect
                              options={damageTypes}
                              optionLabel="name"
                              optionValue="value"
                              id={input.name}
                              value={selectedDamages.map(e => e?.value)}
                              onChange={e => {
                                selectDamage(
                                  e,
                                  form.getState().values?.noDamages
                                );
                              }}
                              onFocus={() => handleFocus("damages")}
                              filter
                              showSelectAll={false}
                              onBlur={input.onBlur}
                              className={classNames({
                                "p-invalid": isFormFieldValid(meta)
                              })}
                            />
                            <label
                              htmlFor={input.name}
                              className={classNames({
                                "p-error": isFormFieldValid(meta, "damages")
                              })}
                            >
                              Damages*
                            </label>
                          </span>
                          {getFormErrorMessage(meta)}
                        </div>
                      )}
                    />
                  )}
                <Field
                  name="titleBrand"
                  render={({ input, meta }) => (
                    <div className="field">
                      <span className="p-float-label">
                        {(selectedDamages == null ||
                          selectedDamages?.length === 0) &&
                        !form.getState().values.noDamages ? (
                          <InputText
                            id={input.name}
                            value={"Please Select Damages"}
                            disabled={true}
                          ></InputText>
                        ) : !form.getState().values.noDamages &&
                          availableTitleBrands?.length === 1 &&
                          titleBrand?.label === "Fire" ? (
                          <InputText
                            id={input.name}
                            value={"Fire"}
                            disabled={true}
                          ></InputText>
                        ) : !form.getState().values.noDamages &&
                          availableTitleBrands?.length === 1 &&
                          titleBrand?.label === "Flood" ? (
                          <InputText
                            id={input.name}
                            value={"Flood"}
                            disabled={true}
                          ></InputText>
                        ) : (
                          <Dropdown
                            id={input.name}
                            options={availableTitleBrands}
                            {...input}
                            value={titleBrand?.value}
                            onChange={e => selectTitleBrand(e.value)}
                            onFocus={() => handleFocus("titleBrand")}
                            className={classNames({
                              "p-invalid": isFormFieldValid(meta)
                            })}
                          />
                        )}
                        <label
                          htmlFor={input.name}
                          className={classNames({
                            "p-error": isFormFieldValid(meta)
                          })}
                        >
                          Requested Title Brand*
                        </label>
                      </span>
                      {getFormErrorMessage(meta)}
                    </div>
                  )}
                />
                <Field
                  name="titleType"
                  render={({ input, meta }) => (
                    <div className="field">
                      <span className="p-float-label">
                        {titleBrand == null ? (
                          <InputText
                            id={input.name}
                            value={"Please Select Title Brand"}
                            disabled={true}
                          ></InputText>
                        ) : availableTitleTypes.length === 1 ? (
                          <InputText
                            id={input.name}
                            value={availableTitleTypes[0].label}
                            disabled={true}
                          ></InputText>
                        ) : (
                          <Dropdown
                            id={input.name}
                            options={availableTitleTypes}
                            {...input}
                            value={titleType?.value}
                            onChange={e => selectTitleType(e.value)}
                            onFocus={() => handleFocus("titleType")}
                            className={classNames({
                              "p-invalid": isFormFieldValid(meta)
                            })}
                          />
                        )}
                        <label
                          htmlFor={input.name}
                          className={classNames({
                            "p-error": isFormFieldValid(meta)
                          })}
                        >
                          Requested Title Type*
                        </label>
                      </span>
                      {getFormErrorMessage(meta)}
                    </div>
                  )}
                />

                <FormItem
                  name={"titleTransferDate"}
                  label={"Title Transfer Date"}
                  type={"date"}
                  h={formItemHelpers}
                  extra={{
                    value: titleTransferDate || new Date(),
                    onChange: e => setTitleTransferDate(e.value)
                  }}
                />
                <FormItem
                  name={"titleNumber"}
                  label={"Current Title Number"}
                  h={formItemHelpers}
                />
                <h3>Acquiring Party Information</h3>
                <FormItem
                  name={"acquiringPartyName"}
                  label={"Acquiring Party Name"}
                  h={formItemHelpers}
                />
                <FormItem
                  name={"acquiringPartyEmail"}
                  label={"Acquiring Party Email"}
                  h={formItemHelpers}
                />

                <div
                  className={`AddressGrouper ${
                    getAddressErrorMessage("acquiringPartyMailingAddress", form)
                      ? "AddressGroupError"
                      : ""
                  }`}
                  onFocus={() => handleFocus("acquiringPartyMailingAddress")}
                >
                  <h4 className={"GroupSectionHeader"}>
                    Acquiring Party Mailing Address
                  </h4>
                  <p>Where title is shipped to</p>
                  <NewAddressForm
                    formPrefix={"acquiringPartyMailingAddress"}
                    addressChoices={
                      carrier?.userRemittanceAddresses
                        ? [
                            ...carrier.titleRemittanceAddresses,
                            {
                              name: "User Address",
                              streetAddress: user?.mailingStreetAddress,
                              streetAddress2: user?.mailingStreetAddress2,
                              city: user?.mailingCity,
                              state: states?.find(
                                state =>
                                  state.stateInitials === user?.mailingState
                              ),
                              zipCode: user?.mailingZipCode
                            }
                          ]
                        : carrier.titleRemittanceAddresses
                    }
                    validate={validate}
                    buildAddressStringFromObject={buildAddressStringFromObject}
                    states={states}
                    creatable
                    setTraId={setTraId}
                  />
                  <span>
                    {getAddressErrorMessage(
                      "acquiringPartyMailingAddress",
                      form
                    )}
                  </span>
                </div>

                {/* Don't allow addresses to match if mailing address in west virginia*/}
                {form.getState().values.acquiringPartyMailingAddress?.state
                  ?.stateName !== "West Virginia" && (
                  <div className={"CheckboxRow"}>
                    <Checkbox
                      inputId="sameAsMailing"
                      onChange={e => {
                        if (e.checked) {
                          setPhysicalSameAsMailing(true);
                          form.change(
                            "acquiringPartyPhysicalAddress",
                            form.getState().values.acquiringPartyMailingAddress
                          );
                        } else {
                          setPhysicalSameAsMailing(false);
                        }
                      }}
                      checked={physicalSameAsMailing}
                    />
                    <label htmlFor="sameAsMailing">
                      {" "}
                      Physical Address same as Mailing
                    </label>
                  </div>
                )}

                <div
                  className={`AddressGrouper ${
                    getAddressErrorMessage(
                      "acquiringPartyPhysicalAddress",
                      form
                    )
                      ? "AddressGroupError"
                      : ""
                  }`}
                  onFocus={() => handleFocus("acquiringPartyPhysicalAddress")}
                >
                  <h4 className={"GroupSectionHeader"}>
                    Acquiring Party Physical Address
                  </h4>
                  <p style={{ color: "red", textAlign: "center" }}>
                    Must not be an address in WV
                  </p>
                  {physicalSameAsMailing &&
                  form.getState().values.acquiringPartyMailingAddress?.state
                    ?.stateName !== "West Virginia" ? (
                    <InputText
                      id="acquiringPartyPhysicalAddress"
                      value={buildAddressStringFromObject(
                        form.getState().values.acquiringPartyMailingAddress
                      )}
                      disabled={true}
                    />
                  ) : (
                    <NewAddressForm
                      formPrefix={"acquiringPartyPhysicalAddress"}
                      validate={validate}
                      addressChoices={
                        carrier?.userRemittanceAddresses
                          ? [
                              ...carrier.titleRemittanceAddresses,
                              {
                                name: "User Address",
                                streetAddress: user?.mailingStreetAddress,
                                streetAddress2: user?.mailingStreetAddress2,
                                city: user?.mailingCity,
                                state: states?.find(
                                  state =>
                                    state.stateInitials === user?.mailingState
                                ),
                                zipCode: user?.mailingZipCode
                              }
                            ]
                          : carrier.titleRemittanceAddresses
                      }
                      initial={
                        physicalSameAsMailing
                          ? form.getState().values.acquiringPartyMailingAddress
                          : {}
                      }
                      buildAddressStringFromObject={
                        buildAddressStringFromObject
                      }
                      hideWestVirginia // this is the weirdest prop I've ever made
                      states={states}
                      creatable
                      setTraId={setTraId}
                    />
                  )}
                  <span>
                    {getAddressErrorMessage(
                      "acquiringPartyPhysicalAddress",
                      form
                    )}
                  </span>
                </div>

                <h3>Disposing Party Information</h3>

                <FormItem
                  name={"lenderHoldsTitle"}
                  label={"Lender or DMV holds title"}
                  h={formItemHelpers}
                  type={"checkbox"}
                />

                <FormItem
                  name={"disposingPartyName"}
                  label={"Disposing Party Name"}
                  h={formItemHelpers}
                />

                <div
                  className={`AddressGrouper ${
                    getAddressErrorMessage("disposingPartyAddress", form)
                      ? "AddressGroupError"
                      : ""
                  }`}
                  onFocus={() => handleFocus("disposingPartyAddress")}
                >
                  <h4 className={"GroupSectionHeader"}>
                    Disposing Party Address
                  </h4>
                  <NewAddressForm
                    formPrefix={"disposingPartyAddress"}
                    validate={validate}
                    buildAddressStringFromObject={buildAddressStringFromObject}
                    states={states}
                  />
                  <span>
                    {getAddressErrorMessage("disposingPartyAddress", form)}
                  </span>
                </div>
              </>
            )}
            <h2 className={"SubsectionHeader"}>Optional Data</h2>

            <FormItem
              name={"ownerRetained"}
              label={"Owner interested in retaining"}
              type={"checkbox"}
              h={formItemHelpers}
            />
            <FormItem
              name={"vehicleLocation"}
              label={"Current Vehicle Location"}
              h={formItemHelpers}
            />
            {(mode === "create" ||
              mode === "newxTitle" ||
              mode === "orders") && (
              <>
                <div className={"SubmitClaimRow"}>
                  <Button
                    label={"Documents"}
                    className={"PrevButton"}
                    onClick={() => {
                      setFullClaimInfo(form.getState().values);
                      goBack(form);
                    }}
                    icon={"pi pi-arrow-left"}
                    iconPos={"left"}
                    loading={submitting}
                  />
                  <Button
                    label={mode === "orders" ? "Place Orders" : "Create Claim"}
                    type={"submit"}
                    onClick={() => {
                      submitFullClaimInfo(form).then();
                    }}
                    className={"NextButton"}
                    icon={"pi pi-arrow-right"}
                    iconPos={"right"}
                    loading={submitting}
                  />
                </div>
                {testing && (
                  <Button
                    label={"😎"}
                    className={"DevButton"}
                    onClick={() => devPopulate(form)}
                    iconPos={"left"}
                  />
                )}
              </>
            )}
            {mode === "edit" && (
              <>
                <div className={"SubmitClaimRow"}>
                  <Button
                    label={"Back"}
                    className={"NextButton"}
                    onClick={() => {
                      setFullClaimInfo(form.getState().values);
                      goBack(form);
                    }}
                    icon={"pi pi-arrow-left"}
                    iconPos={"left"}
                    loading={submitting}
                  />
                  <Button
                    label={"Done"}
                    type={"Submit"}
                    onClick={e => {
                      submitFullClaimInfo(form).then();
                    }}
                    className={"NextButton"}
                    icon={"pi pi-arrow-right"}
                    iconPos={"right"}
                    loading={submitting}
                  />
                </div>
              </>
            )}
          </form>
        )}
      />
    </div>
  );
};

export default FullClaimInfoForm;
